/**
 * 🏗️ System Dashboard - Premium Command Center
 * Schöne große Buttons, Akkordeon-Menüs, Mobile-First
 */

import React, { useState, useEffect } from 'react';
import { 
  Server, GitBranch, FolderTree, FileCode, Activity,
  ChevronRight, ChevronDown, ArrowLeft, Check,
  AlertTriangle, Loader2, Clock, Zap, Database,
  FileText, Folder, RotateCcw, X, RefreshCw,
  Shield, Cpu, HardDrive, Wifi, Code, Box, Search
} from 'lucide-react';
import { API_URL } from '../../config';

// ============================================
// MAIN DASHBOARD
// ============================================
export default function SystemDashboard() {
  const [activeSection, setActiveSection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [codeStats, setCodeStats] = useState(null);
  const [debugAnalysis, setDebugAnalysis] = useState(false);
  const [debugToggling, setDebugToggling] = useState(false);

  // Debug-Status von API laden
  useEffect(() => {
    fetch('/api/system/status')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setDebugAnalysis(!!data.debug_image_analysis); })
      .catch(() => {});
  }, []);

  // Code-Stats laden
  useEffect(() => {
    const loadCodeStats = async () => {
      try {
        const token = localStorage.getItem('snapshot_token');
        const res = await fetch(`${API_URL}/api/admin/system/code-stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          console.log('Code stats loaded:', data);
          setCodeStats(data);
        } else {
          console.error('Code stats failed:', res.status);
        }
      } catch (err) {
        console.error('Code stats error:', err);
      }
    };
    loadCodeStats();
  }, []);

  const doSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    setSearchResults(null);
    
    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/admin/system/search`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query: searchQuery })
      });
      const data = await res.json();
      setSearchResults(data);
    } catch (err) {
      console.error(err);
    } finally {
      setSearching(false);
    }
  };

  // Main Menu Items
  const menuItems = [
    {
      id: 'audit',
      icon: Shield,
      title: 'Code Audit',
      subtitle: 'Live vs Legacy Code',
      color: 'from-amber-500 to-orange-600',
      badge: '🧹'
    },
    {
      id: 'files',
      icon: FolderTree,
      title: 'Datei-Explorer',
      subtitle: 'Backend-Code durchsuchen',
      color: 'from-blue-500 to-cyan-600',
      badge: '📁'
    },
    {
      id: 'status',
      icon: Activity,
      title: 'System Status',
      subtitle: 'Features & Dokumentation',
      color: 'from-purple-500 to-pink-600',
      badge: '📊'
    },
    {
      id: 'api',
      icon: Code,
      title: 'API Routes',
      subtitle: 'Alle Endpoints anzeigen',
      color: 'from-orange-500 to-red-600',
      badge: '🔌'
    },
    {
      id: 'features',
      icon: Box,
      title: 'Offene Features / To-Do Liste',
      subtitle: 'Angefangen, nicht fertig',
      color: 'from-pink-500 to-rose-600',
      badge: '🚧'
    }
  ];

  // If a section is active, show that section
  if (activeSection) {
    return (
      <div className="min-h-screen">
        {/* Back Button */}
        <button
          onClick={() => setActiveSection(null)}
          className="flex items-center gap-2 text-white/70 hover:text-white mb-6 p-2 -ml-2 rounded-xl hover:bg-white/5 transition-all touch-manipulation"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Zurück zum Dashboard</span>
        </button>

        {/* Git History entfernt */}
        {activeSection === 'audit' && <AuditSection />}
        {activeSection === 'files' && <FilesSection />}
        {activeSection === 'status' && <StatusSection />}
        {activeSection === 'api' && <ApiSection />}
        {activeSection === 'features' && <OpenFeaturesSection />}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center py-4">
        <h1 className="text-2xl lg:text-3xl font-bold text-white flex items-center justify-center gap-3">
          <span className="text-3xl">🏗️</span>
          <span>System Dashboard</span>
        </h1>
        <p className="text-white/50 mt-2">Command Center für Entwicklung & Debugging</p>
      </div>

      {/* 🔍 Debug: Bildanalyse-Toggle */}
      <div className="rounded-2xl bg-gradient-to-br from-orange-500/10 to-amber-500/10 border border-orange-500/20 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
              <span className="text-xl">🔍</span>
            </div>
            <div>
              <h3 className="text-white font-bold text-sm">Debug: Bildanalyse</h3>
              <p className="text-white/40 text-xs">Zeigt Analyse-Daten im Bild-Viewer (alle Accounts)</p>
            </div>
          </div>
          <button
            disabled={debugToggling}
            onClick={async () => {
              setDebugToggling(true);
              try {
                const token = localStorage.getItem('snapshot_token');
                const res = await fetch(`${API_URL}/api/admin/debug/image-analysis`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                });
                if (res.ok) {
                  const data = await res.json();
                  setDebugAnalysis(!!data.debug_image_analysis);
                }
              } catch (e) { console.error('Debug toggle failed:', e); }
              setDebugToggling(false);
            }}
            className={`relative w-14 h-7 rounded-full transition-colors ${debugAnalysis ? 'bg-orange-500' : 'bg-white/10'}`}
          >
            <div className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform ${debugAnalysis ? 'translate-x-7' : 'translate-x-0.5'}`} />
          </button>
        </div>
      </div>

      {/* 🔍 GLOBAL SEARCH */}
      <div className="rounded-2xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-500 flex items-center justify-center">
            <span className="text-xl">🔍</span>
          </div>
          <div>
            <h3 className="text-white font-bold">Code Intelligence</h3>
            <p className="text-white/50 text-xs">Durchsuche den Live-Code</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="z.B. Remix, Favoriten, Video..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && doSearch()}
            className="flex-1 p-4 rounded-xl bg-black/30 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-indigo-500/50 text-lg"
          />
          <button
            onClick={doSearch}
            disabled={searching || !searchQuery.trim()}
            className="px-6 rounded-xl bg-indigo-500 text-white font-bold hover:bg-indigo-600 transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {searching ? <Loader2 className="w-5 h-5 animate-spin" /> : <span>🔍</span>}
            <span className="hidden lg:inline">Suchen</span>
          </button>
        </div>
      </div>

      {/* Search Results */}
      {searchResults && (
        <SearchResultsView results={searchResults} onClose={() => setSearchResults(null)} />
      )}

      {/* Code Statistics */}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/30 text-center">
          <p className="text-blue-400 text-2xl font-bold">
            {codeStats?.backend?.lines?.toLocaleString() || '...'}
          </p>
          <p className="text-blue-400/70 text-sm">Backend</p>
          <p className="text-white/40 text-xs">{codeStats?.backend?.files || 0} Dateien</p>
        </div>
        <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30 text-center">
          <p className="text-green-400 text-2xl font-bold">
            {codeStats?.frontend?.lines?.toLocaleString() || '...'}
          </p>
          <p className="text-green-400/70 text-sm">Frontend</p>
          <p className="text-white/40 text-xs">{codeStats?.frontend?.files || 0} Dateien</p>
        </div>
        <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/30 text-center">
          <p className="text-purple-400 text-2xl font-bold">
            {codeStats?.total?.lines?.toLocaleString() || '...'}
          </p>
          <p className="text-purple-400/70 text-sm">Gesamt</p>
          <p className="text-white/40 text-xs">{codeStats?.total?.files || 0} Dateien</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <QuickStat icon={Server} label="Backend" value="Online" color="green" />
        <QuickStat icon={Database} label="MongoDB" value="Verbunden" color="green" />
        <QuickStat icon={Shield} label="Auth" value="Aktiv" color="green" />
        <QuickStat icon={Zap} label="API" value="OK" color="green" />
      </div>

      {/* Main Menu - Big Beautiful Buttons */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {menuItems.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              className={`relative overflow-hidden rounded-2xl p-6 text-left transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] touch-manipulation group`}
            >
              {/* Gradient Background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-20 group-hover:opacity-30 transition-opacity`} />
              
              {/* Content */}
              <div className="relative flex items-start gap-4">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center shadow-lg`}>
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-white">{item.title}</h3>
                    <span className="text-xl">{item.badge}</span>
                  </div>
                  <p className="text-white/60 text-sm mt-1">{item.subtitle}</p>
                </div>
                <ChevronRight className="w-6 h-6 text-white/30 group-hover:text-white/60 group-hover:translate-x-1 transition-all" />
              </div>

              {/* Border */}
              <div className="absolute inset-0 rounded-2xl border border-white/10 group-hover:border-white/20 transition-colors" />
            </button>
          );
        })}
      </div>

      {/* Footer Info */}
      <div className="text-center text-white/30 text-sm py-4">
        snapshotpro.ai • Amazon EC2 • FastAPI + React
      </div>
    </div>
  );
}

// ============================================
// SEARCH RESULTS VIEW
// ============================================
function SearchResultsView({ results, onClose }) {
  const [expandedFile, setExpandedFile] = useState(null);

  if (!results) return null;

  const hasResults = results.files?.length > 0 || results.routes?.length > 0;

  return (
    <div className="rounded-2xl border border-indigo-500/30 bg-black/30 overflow-hidden">
      {/* Header */}
      <div className="p-4 bg-indigo-500/10 border-b border-indigo-500/30 flex items-center justify-between">
        <div>
          <h3 className="text-white font-bold flex items-center gap-2">
            <span>🔍</span>
            Suchergebnis: "{results.query}"
          </h3>
          <p className="text-white/50 text-sm mt-1">
            {results.total_matches} Treffer in {results.files?.length || 0} Dateien
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-xl hover:bg-white/10 text-white/50 hover:text-white transition-all"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {!hasResults ? (
        <div className="p-8 text-center">
          <span className="text-4xl mb-4 block">🔎</span>
          <p className="text-white/50">Keine Treffer für "{results.query}"</p>
          <p className="text-white/30 text-sm mt-2">Versuche einen anderen Begriff</p>
        </div>
      ) : (
        <div className="divide-y divide-white/10">
          {/* 🧠 LOGIK */}
          <div className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <span>🧠</span>
              </div>
              <h4 className="text-white font-bold">Logik</h4>
            </div>
            <div className="bg-purple-500/10 rounded-xl p-4 text-white/80 text-sm whitespace-pre-wrap">
              {results.logic || 'Keine Beschreibung verfügbar'}
            </div>
          </div>

          {/* 🔌 ROUTEN */}
          {results.routes?.length > 0 && (
            <div className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <span>🔌</span>
                </div>
                <h4 className="text-white font-bold">API-Routen ({results.routes.length})</h4>
              </div>
              <div className="space-y-2">
                {results.routes.map((route, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/5">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${
                      route.method === 'GET' ? 'bg-green-500/20 text-green-400' :
                      route.method === 'POST' ? 'bg-blue-500/20 text-blue-400' :
                      route.method === 'PUT' ? 'bg-yellow-500/20 text-yellow-400' :
                      route.method === 'DELETE' ? 'bg-red-500/20 text-red-400' :
                      'bg-purple-500/20 text-purple-400'
                    }`}>
                      {route.method}
                    </span>
                    <code className="text-white/70 text-sm font-mono flex-1">{route.path}</code>
                    <span className="text-white/30 text-xs">{route.file}:{route.line}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 📁 DATEIEN */}
          {results.files?.length > 0 && (
            <div className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <span>📁</span>
                </div>
                <h4 className="text-white font-bold">Dateien ({results.files.length})</h4>
              </div>
              <div className="space-y-2">
                {results.files.map((file, i) => (
                  <div key={i} className="rounded-xl bg-white/5 overflow-hidden">
                    <button
                      onClick={() => setExpandedFile(expandedFile === i ? null : i)}
                      className="w-full p-3 flex items-center gap-3 hover:bg-white/5 transition-all"
                    >
                      <FileCode className="w-5 h-5 text-yellow-400" />
                      <span className="text-white/80 text-sm font-mono flex-1 text-left">{file.path}</span>
                      <span className="text-white/30 text-xs">{file.total_matches} Treffer</span>
                      <ChevronDown className={`w-4 h-4 text-white/30 transition-transform ${expandedFile === i ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {expandedFile === i && file.matches && (
                      <div className="px-3 pb-3 space-y-1">
                        {file.matches.map((match, j) => (
                          <div key={j} className="flex items-start gap-2 p-2 rounded-lg bg-black/30 text-xs font-mono">
                            <span className="text-white/30 w-10">:{match.line}</span>
                            <span className="text-white/60 flex-1">{match.content}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// QUICK STAT COMPONENT
// ============================================
function QuickStat({ icon: Icon, label, value, color }) {
  const colorClasses = {
    green: 'bg-green-500/20 text-green-400 border-green-500/30',
    yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    red: 'bg-red-500/20 text-red-400 border-red-500/30',
  };

  return (
    <div className={`rounded-xl p-3 border ${colorClasses[color]} flex items-center gap-3`}>
      <Icon className="w-5 h-5" />
      <div>
        <div className="text-xs opacity-70">{label}</div>
        <div className="font-bold text-sm">{value}</div>
      </div>
    </div>
  );
}

// ============================================
// GIT SECTION
// ============================================
function GitSection() {
  const [commits, setCommits] = useState([]);
  const [gitError, setGitError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCommit, setExpandedCommit] = useState(null);
  const [commitData, setCommitData] = useState({});
  const [rollbackModal, setRollbackModal] = useState(null);
  const [rolling, setRolling] = useState(false);

  useEffect(() => {
    fetchCommits();
  }, []);

  const fetchCommits = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/admin/system/git/commits?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setCommits(data.commits || []);
      // Fehler nur anzeigen wenn keine Commits
      if (data.error && (!data.commits || data.commits.length === 0)) {
        setGitError(data.error);
      } else {
        setGitError(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleCommit = async (hash) => {
    if (expandedCommit === hash) {
      setExpandedCommit(null);
      return;
    }
    
    setExpandedCommit(hash);
    
    if (!commitData[hash]) {
      setCommitData(prev => ({ ...prev, [hash]: { loading: true } }));
      
      try {
        const token = localStorage.getItem('snapshot_token');
        const [diffRes, noteRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/system/git/diff/${hash}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/admin/system/git/note/${hash}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);
        
        const diff = await diffRes.json();
        const note = await noteRes.json();
        
        setCommitData(prev => ({
          ...prev,
          [hash]: { diff, note: note.found ? note.note : null, loading: false }
        }));
      } catch (err) {
        setCommitData(prev => ({ ...prev, [hash]: { error: true, loading: false } }));
      }
    }
  };

  const doRollback = async () => {
    setRolling(true);
    try {
      const token = localStorage.getItem('snapshot_token');
      await fetch(`${API_URL}/api/admin/system/git/rollback`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ commit_hash: rollbackModal.hash, confirm: true })
      });
      alert('✅ Rollback erfolgreich!');
      setRollbackModal(null);
      fetchCommits();
    } catch (err) {
      alert('❌ Rollback fehlgeschlagen');
    } finally {
      setRolling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-10 h-10 animate-spin text-[#10B981]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
            <GitBranch className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Git History</h2>
            <p className="text-white/50 text-sm">{commits.length} Commits</p>
          </div>
        </div>
        <button
          onClick={fetchCommits}
          className="p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-all touch-manipulation"
        >
          <RefreshCw className="w-5 h-5 text-white/50" />
        </button>
      </div>

      {/* Git Error Hinweis */}
      {gitError && (
        <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
          <p className="text-yellow-500 font-medium">⚠️ {gitError}</p>
          <p className="text-white/50 text-sm mt-1">GitHub Personal Access Token benötigt für private Repos</p>
        </div>
      )}

      {/* Commit List */}
      <div className="space-y-3">
        {commits.length === 0 && !gitError && (
          <div className="p-4 bg-white/5 rounded-xl text-center">
            <p className="text-white/50">Lade Commits...</p>
          </div>
        )}
        {commits.map((commit, index) => {
          const isExpanded = expandedCommit === commit.hash;
          const data = commitData[commit.hash];
          
          return (
            <div 
              key={commit.hash}
              className={`rounded-2xl border transition-all overflow-hidden ${
                isExpanded 
                  ? 'border-[#10B981]/50 bg-[#10B981]/5' 
                  : 'border-white/10 bg-white/5 hover:bg-white/10'
              }`}
            >
              {/* Commit Header */}
              <button
                onClick={() => toggleCommit(commit.hash)}
                className="w-full p-4 flex items-center gap-4 text-left touch-manipulation"
              >
                {/* Number Badge */}
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm ${
                  index === 0 
                    ? 'bg-[#10B981] text-black' 
                    : 'bg-white/10 text-white/50'
                }`}>
                  #{commits.length - index}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="text-[#10B981] font-mono text-sm font-bold">{commit.hash_short}</code>
                    {index === 0 && (
                      <span className="px-2 py-0.5 rounded-full bg-[#10B981]/20 text-[#10B981] text-xs font-medium">
                        AKTUELL
                      </span>
                    )}
                  </div>
                  <p className="text-white text-sm mt-1 line-clamp-1">{commit.message}</p>
                  <p className="text-white/40 text-xs mt-1">
                    {commit.date ? new Date(commit.date).toLocaleString('de-DE', {
                      day: '2-digit', month: '2-digit', year: '2-digit',
                      hour: '2-digit', minute: '2-digit'
                    }) : ''}
                  </p>
                </div>

                {/* Expand Icon */}
                <ChevronDown className={`w-6 h-6 text-white/30 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
              </button>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="px-4 pb-4 space-y-4 animate-fadeIn">
                  {data?.loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-[#10B981]" />
                    </div>
                  ) : (
                    <>
                      {/* Was wurde gemacht? */}
                      <div className="rounded-xl bg-blue-500/10 border border-blue-500/30 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="w-5 h-5 text-blue-400" />
                          <span className="text-blue-400 font-bold">Was wurde gemacht?</span>
                        </div>
                        <p className="text-white/80 text-sm whitespace-pre-wrap">
                          {data?.note || '📝 Noch keine Zusammenfassung. Moltbot fügt diese nach Deploys hinzu!'}
                        </p>
                      </div>

                      {/* Geänderte Dateien */}
                      {data?.diff?.files?.length > 0 && (
                        <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                          <h4 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                            <FolderTree className="w-4 h-4 text-yellow-400" />
                            Geänderte Dateien ({data.diff.files.length})
                          </h4>
                          <div className="space-y-2">
                            {data.diff.files.slice(0, 8).map((file, i) => (
                              <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-black/20">
                                <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                                  file.status === 'added' ? 'bg-green-500/20 text-green-400' :
                                  file.status === 'deleted' ? 'bg-red-500/20 text-red-400' :
                                  'bg-yellow-500/20 text-yellow-400'
                                }`}>
                                  {file.status === 'added' ? '+' : file.status === 'deleted' ? '−' : '~'}
                                </span>
                                <span className="text-white/70 text-sm font-mono truncate flex-1">{file.file}</span>
                              </div>
                            ))}
                            {data.diff.files.length > 8 && (
                              <p className="text-white/40 text-xs text-center py-2">
                                + {data.diff.files.length - 8} weitere Dateien
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Code Changes Preview */}
                      {data?.diff?.diffs?.length > 0 && data.diff.diffs.length <= 5 && (
                        <div className="rounded-xl bg-black/30 border border-white/10 overflow-hidden">
                          <div className="px-4 py-3 bg-white/5 border-b border-white/10">
                            <h4 className="text-white font-bold text-sm flex items-center gap-2">
                              <Code className="w-4 h-4 text-purple-400" />
                              Code-Änderungen
                            </h4>
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {data.diff.diffs.slice(0, 2).map((fileDiff, i) => (
                              <div key={i}>
                                <div className="px-4 py-2 bg-white/5 text-white/50 text-xs font-mono border-b border-white/5">
                                  {fileDiff.file}
                                </div>
                                {fileDiff.hunks?.slice(0, 1).map((hunk, j) => (
                                  <div key={j} className="text-xs font-mono">
                                    {hunk.lines?.slice(0, 8).map((line, k) => (
                                      <div key={k} className={`px-4 py-0.5 ${
                                        line.type === 'add' ? 'bg-green-500/20 text-green-300' :
                                        line.type === 'remove' ? 'bg-red-500/20 text-red-300' :
                                        'text-white/30'
                                      }`}>
                                        {line.type === 'add' ? '+' : line.type === 'remove' ? '-' : ' '} {line.content}
                                      </div>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Rollback Button */}
                      {index > 0 && (
                        <button
                          onClick={() => setRollbackModal(commit)}
                          className="w-full p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 font-bold flex items-center justify-center gap-3 hover:bg-red-500/20 transition-all touch-manipulation"
                        >
                          <RotateCcw className="w-5 h-5" />
                          Zu diesem Stand zurücksetzen
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Rollback Modal */}
      {rollbackModal && (
        <div className="fixed inset-0 bg-black/90 flex items-end lg:items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-3xl w-full max-w-md border border-white/10 overflow-hidden animate-slideUp">
            {/* Header */}
            <div className="p-6 text-center border-b border-white/10">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-white">Rollback bestätigen?</h3>
              <p className="text-white/50 text-sm mt-2">Alle neueren Änderungen gehen verloren!</p>
            </div>

            {/* Commit Info */}
            <div className="p-4 mx-4 my-4 rounded-xl bg-black/30">
              <div className="flex items-center gap-2 mb-2">
                <code className="text-[#10B981] font-mono font-bold">{rollbackModal.hash_short}</code>
              </div>
              <p className="text-white/70 text-sm">{rollbackModal.message}</p>
            </div>

            {/* Buttons */}
            <div className="p-4 flex gap-3">
              <button
                onClick={() => setRollbackModal(null)}
                className="flex-1 p-4 rounded-xl bg-white/10 text-white font-bold hover:bg-white/20 transition-all touch-manipulation"
              >
                Abbrechen
              </button>
              <button
                onClick={doRollback}
                disabled={rolling}
                className="flex-1 p-4 rounded-xl bg-red-500 text-white font-bold hover:bg-red-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50 touch-manipulation"
              >
                {rolling ? <Loader2 className="w-5 h-5 animate-spin" /> : <RotateCcw className="w-5 h-5" />}
                Zurücksetzen
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        .animate-slideUp { animation: slideUp 0.3s ease-out; }
      `}</style>
    </div>
  );
}

// ============================================
// FILES SECTION
// ============================================
function FilesSection() {
  const [currentPath, setCurrentPath] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fileContent, setFileContent] = useState(null);

  useEffect(() => {
    fetchFiles(currentPath);
  }, [currentPath]);

  const fetchFiles = async (path) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/admin/system/files?path=${encodeURIComponent(path)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setItems(data.items || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openFile = async (path) => {
    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/admin/system/files/content?path=${encodeURIComponent(path)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setFileContent(await res.json());
    } catch (err) {
      alert('Datei kann nicht geöffnet werden');
    }
  };

  // File Viewer
  if (fileContent) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => setFileContent(null)}
          className="flex items-center gap-2 text-white/70 hover:text-white p-2 -ml-2 rounded-xl hover:bg-white/5 transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Zurück</span>
        </button>
        
        <div className="rounded-2xl border border-white/10 overflow-hidden">
          <div className="p-4 bg-white/5 border-b border-white/10">
            <h3 className="text-white font-bold">{fileContent.filename}</h3>
            <p className="text-white/50 text-sm">{fileContent.lines} Zeilen</p>
          </div>
          <div className="p-4 bg-black/50 max-h-[60vh] overflow-auto">
            <pre className="text-xs font-mono text-white/80 whitespace-pre-wrap">{fileContent.content}</pre>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-10 h-10 animate-spin text-[#10B981]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
          <FolderTree className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">Datei-Explorer</h2>
          <p className="text-white/50 text-sm font-mono">/{currentPath || 'backend'}</p>
        </div>
      </div>

      {/* Back Button */}
      {currentPath && (
        <button
          onClick={() => {
            const parts = currentPath.split('/').filter(Boolean);
            parts.pop();
            setCurrentPath(parts.join('/'));
          }}
          className="w-full p-4 rounded-xl bg-white/5 border border-white/10 flex items-center gap-3 hover:bg-white/10 transition-all touch-manipulation"
        >
          <ArrowLeft className="w-5 h-5 text-white/50" />
          <span className="text-white/70">Zurück</span>
        </button>
      )}

      {/* File/Folder List */}
      <div className="space-y-2">
        {items.map((item) => (
          <button
            key={item.path}
            onClick={() => {
              if (item.type === 'folder') {
                setCurrentPath(item.path);
              } else {
                openFile(item.path);
              }
            }}
            className="w-full p-4 rounded-xl bg-white/5 border border-white/10 flex items-center gap-4 hover:bg-white/10 transition-all touch-manipulation"
          >
            {item.type === 'folder' ? (
              <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                <Folder className="w-5 h-5 text-yellow-400" />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <FileCode className="w-5 h-5 text-blue-400" />
              </div>
            )}
            <div className="flex-1 text-left min-w-0">
              <p className="text-white font-medium truncate">{item.name}</p>
              {item.type === 'file' && (
                <p className="text-white/40 text-xs">{(item.size / 1024).toFixed(1)} KB</p>
              )}
            </div>
            <ChevronRight className="w-5 h-5 text-white/30" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================
// STATUS SECTION
// ============================================
function StatusSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedFeature, setExpandedFeature] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/admin/system/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStatus(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-10 h-10 animate-spin text-[#10B981]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
          <Activity className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">System Status</h2>
          <p className="text-white/50 text-sm">Features & Dokumentation</p>
        </div>
      </div>

      {/* Feature List */}
      <div className="space-y-3">
        {status?.features?.map((feature, index) => {
          const isExpanded = expandedFeature === index;
          return (
            <div 
              key={index}
              className={`rounded-2xl border transition-all overflow-hidden ${
                isExpanded ? 'border-purple-500/50 bg-purple-500/5' : 'border-white/10 bg-white/5'
              }`}
            >
              <button
                onClick={() => setExpandedFeature(isExpanded ? null : index)}
                className="w-full p-4 flex items-center gap-4 text-left touch-manipulation"
              >
                <span className="text-2xl">{feature.name.split(' ')[0]}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold">{feature.name.replace(/^[^\s]+\s/, '')}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                      feature.status === 'live' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {feature.status.toUpperCase()}
                    </span>
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-white/30 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
              </button>
              
              {isExpanded && (
                <div className="px-4 pb-4 space-y-3 animate-fadeIn">
                  <p className="text-white/70 text-sm">{feature.description}</p>
                  {feature.flow && (
                    <div className="rounded-xl bg-black/30 p-4">
                      <h4 className="text-white/50 text-xs font-bold mb-3">ABLAUF:</h4>
                      {feature.flow.map((step, i) => (
                        <div key={i} className="flex items-start gap-3 py-2">
                          <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs text-white/50">
                            {i + 1}
                          </div>
                          <p className="text-white/70 text-sm flex-1">{step}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <style jsx>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
      `}</style>
    </div>
  );
}

// ============================================
// CODE AUDIT SECTION
// ============================================
function AuditSection() {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('live');
  const [liveFilter, setLiveFilter] = useState('');

  useEffect(() => {
    fetchAudit();
  }, []);

  const fetchAudit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/admin/system/audit`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setAudit(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-10 h-10 animate-spin text-[#10B981]" />
      </div>
    );
  }

  const tabs = [
    { id: 'live', label: '✅ Live', count: audit?.summary?.live_routes || 0 },
    { id: 'test', label: '🧪 Test', count: audit?.test_files?.length || 0 },
    { id: 'legacy', label: '⚠️ Legacy', count: audit?.legacy_files?.length || 0 },
    { id: 'unused', label: '❌ Unbenutzt', count: audit?.unused_routes?.length || 0 },
    { id: 'broken', label: '🔴 Fehlerhaft', count: audit?.broken_routes?.length || 0 },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
          <Shield className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">Code Audit</h2>
          <p className="text-white/50 text-sm">Live vs Legacy/Test Code</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30">
          <p className="text-green-400 text-2xl font-bold">{audit?.summary?.live_routes || 0}</p>
          <p className="text-green-400/70 text-sm">Live Routes</p>
        </div>
        <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
          <p className="text-yellow-400 text-2xl font-bold">{audit?.test_files?.length || 0}</p>
          <p className="text-yellow-400/70 text-sm">Test-Dateien</p>
        </div>
        <div className="p-4 rounded-xl bg-orange-500/10 border border-orange-500/30">
          <p className="text-orange-400 text-2xl font-bold">{audit?.legacy_files?.length || 0}</p>
          <p className="text-orange-400/70 text-sm">Legacy-Dateien</p>
        </div>
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30">
          <p className="text-red-400 text-2xl font-bold">{audit?.unused_routes?.length || 0}</p>
          <p className="text-red-400/70 text-sm">Unbenutzte Routes</p>
        </div>
        <div className="p-4 rounded-xl bg-pink-500/10 border border-pink-500/30">
          <p className="text-pink-400 text-2xl font-bold">{audit?.broken_routes?.length || 0}</p>
          <p className="text-pink-400/70 text-sm">Fehlerhafte Routes</p>
        </div>
      </div>

      {/* Size Info */}
      {(audit?.summary?.total_test_size_kb > 0 || audit?.summary?.total_legacy_size_kb > 0) && (
        <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
          <p className="text-amber-400 text-sm">
            💡 <strong>Aufräum-Potenzial:</strong>{' '}
            {audit?.summary?.total_test_size_kb || 0} KB Test-Code + {audit?.summary?.total_legacy_size_kb || 0} KB Legacy-Code
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-xl font-medium text-sm whitespace-nowrap transition-all ${
              activeTab === tab.id 
                ? 'bg-[#10B981] text-black' 
                : 'bg-white/5 text-white/70 hover:bg-white/10'
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Live Filter */}
      {activeTab === 'live' && (
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
          <input
            type="text"
            value={liveFilter}
            onChange={(e) => setLiveFilter(e.target.value)}
            placeholder="Route filtern... (z.B. favorite, video, avatar)"
            className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-[#10B981] focus:outline-none"
          />
          {liveFilter && (
            <button
              onClick={() => setLiveFilter('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
            >
              ✕
            </button>
          )}
        </div>
      )}

      {/* Filter Results Count - ALWAYS show when live tab */}
      {activeTab === 'live' && (
        <p className="text-white/50 text-sm">
          {liveFilter ? (
            <>🔍 {audit?.live_routes?.filter(route => 
              route.path.toLowerCase().includes(liveFilter.toLowerCase())
            ).length || 0} Routes gefunden für "{liveFilter}"</>
          ) : (
            <>📋 {audit?.live_routes?.length || 0} Routes geladen (tippe zum Filtern)</>
          )}
        </p>
      )}

      {/* Content */}
      <div className="space-y-2">
        {activeTab === 'live' && (
          audit?.live_routes?.filter(route => 
            !liveFilter || route.path.toLowerCase().includes(liveFilter.toLowerCase())
          ).map((route, i) => (
            <div key={i} className="p-3 rounded-xl bg-green-500/5 border border-green-500/20 flex items-center gap-3">
              <span className="px-2 py-1 rounded text-xs font-bold bg-green-500/20 text-green-400">
                {route.methods?.[0] || route.method || 'GET'}
              </span>
              <code className="text-white/70 text-sm font-mono flex-1 truncate">{route.path}</code>
              {route.file && <span className="text-white/30 text-xs hidden sm:block">{route.file}</span>}
            </div>
          ))
        )}

        {activeTab === 'test' && audit?.test_files?.map((file, i) => (
          <div key={i} className="p-3 rounded-xl bg-yellow-500/5 border border-yellow-500/20 flex items-center gap-3">
            <span className="text-xl">🧪</span>
            <code className="text-white/70 text-sm font-mono flex-1">{file.path}</code>
            <span className="text-yellow-400/70 text-xs">{file.size_kb} KB</span>
          </div>
        ))}

        {activeTab === 'legacy' && audit?.legacy_files?.map((file, i) => (
          <div key={i} className="p-3 rounded-xl bg-orange-500/5 border border-orange-500/20 flex items-center gap-3">
            <span className="text-xl">⚠️</span>
            <code className="text-white/70 text-sm font-mono flex-1">{file.path}</code>
            <span className="text-orange-400/70 text-xs">{file.size_kb} KB</span>
          </div>
        ))}

        {activeTab === 'unused' && audit?.unused_routes?.map((file, i) => (
          <div key={i} className="p-3 rounded-xl bg-red-500/5 border border-red-500/20 flex items-center gap-3">
            <span className="text-xl">❌</span>
            <code className="text-white/70 text-sm font-mono flex-1">{file.path}</code>
            <span className="text-red-400 text-xs">Nicht registriert</span>
          </div>
        ))}

        {activeTab === 'broken' && audit?.broken_routes?.map((route, i) => (
          <div key={i} className="p-3 rounded-xl bg-pink-500/5 border border-pink-500/20 flex items-center gap-3">
            <span className="text-xl">🔴</span>
            <div className="flex-1">
              <code className="text-white/70 text-sm font-mono">{route.method} {route.path}</code>
              {route.error && <p className="text-pink-400/70 text-xs mt-1">{route.error}</p>}
            </div>
            <span className="text-pink-400 text-xs">Fehler</span>
          </div>
        ))}

        {/* Empty States */}
        {activeTab === 'test' && (!audit?.test_files || audit.test_files.length === 0) && (
          <div className="text-center py-8 text-white/50">Keine Test-Dateien gefunden</div>
        )}
        {activeTab === 'legacy' && (!audit?.legacy_files || audit.legacy_files.length === 0) && (
          <div className="text-center py-8 text-white/50">Keine Legacy-Dateien gefunden</div>
        )}
        {activeTab === 'unused' && (!audit?.unused_routes || audit.unused_routes.length === 0) && (
          <div className="text-center py-8 text-green-400">✅ Alle Route-Dateien sind registriert!</div>
        )}
        {activeTab === 'broken' && (!audit?.broken_routes || audit.broken_routes.length === 0) && (
          <div className="text-center py-8 text-green-400">✅ Keine fehlerhaften Routes gefunden!</div>
        )}
      </div>
    </div>
  );
}

// ============================================
// API ROUTES SECTION
// ============================================
function ApiSection() {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    fetchRoutes();
  }, []);

  const fetchRoutes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/admin/system/routes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setRoutes(data.routes || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const methodColors = {
    GET: 'bg-green-500/20 text-green-400 border-green-500/30',
    POST: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    PUT: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    DELETE: 'bg-red-500/20 text-red-400 border-red-500/30',
    PATCH: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  };

  const filteredRoutes = routes.filter(r => 
    r.path.toLowerCase().includes(filter.toLowerCase()) ||
    r.method.toLowerCase().includes(filter.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-10 h-10 animate-spin text-[#10B981]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
          <Code className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">API Routes</h2>
          <p className="text-white/50 text-sm">{routes.length} Endpoints</p>
        </div>
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="🔍 Route suchen..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="w-full p-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-[#10B981]/50"
      />

      {/* Routes List */}
      <div className="space-y-2">
        {filteredRoutes.map((route, i) => (
          <div 
            key={i}
            className="p-4 rounded-xl bg-white/5 border border-white/10 flex items-center gap-3"
          >
            <span className={`px-3 py-1 rounded-lg text-xs font-bold border ${methodColors[route.method] || methodColors.GET}`}>
              {route.method}
            </span>
            <code className="text-white/70 text-sm font-mono truncate flex-1">{route.path}</code>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// 🚧 OPEN FEATURES SECTION
// ============================================
function OpenFeaturesSection() {
  const [expandedFeature, setExpandedFeature] = useState(null);

  // Offene Features / To-Do Liste - hier pflegen wir die Liste
  const openFeatures = [
    {
      id: 'community',
      name: '👥 Community / Social',
      status: 'paused',
      statusLabel: '⏸️ Pausiert',
      description: 'Instagram-ähnliches Social Network für SNAPSHOT User. Posts teilen, liken, kommentieren, folgen.',
      howItWorks: [
        'User erstellt SnapTag (z.B. @SNAP_4821)',
        'Kann Bilder aus Galerie zur Community teilen',
        'Andere User können liken, kommentieren, folgen',
        'Feed zeigt Posts von gefolgten Usern'
      ],
      currentCode: {
        backend: [
          '/opt/backend/routes/community.py - 15 Routes (Profile, Feed, Posts, Follow)',
          'MongoDB Collections: community_profiles, community_posts, community_follows'
        ],
        frontend: [
          'src/components/community/CommunityPage.js',
          'src/components/community/CommunityFeed.js',
          'src/components/community/CommunityPost.js',
          'src/components/community/CommunityProfile.js',
          'src/components/community/ShareToCommunityModal.js'
        ]
      },
      problem: 'Backend-Router hat keinen Prefix! Frontend ruft /api/community/... auf, aber Backend exponiert nur /feed, /posts etc. ohne /api/community.',
      plan: [
        '1. Router-Prefix fixen: prefix="/api/community"',
        '2. Feature testen ob es funktioniert',
        '3. Entscheiden: Weitermachen oder entfernen?'
      ]
    },
    {
      id: 'vehicles',
      name: '🚗 Vehicle Mode',
      status: 'beta',
      statusLabel: '🧪 Beta',
      description: 'Avatar-Training für Autos, Motorräder, etc. Gleiche KI wie für Menschen, aber für Fahrzeuge.',
      howItWorks: [
        'User lädt Fotos von seinem Fahrzeug hoch',
        'Astria trainiert ein Fahrzeug-Modell',
        'User kann Fahrzeug in verschiedenen Szenen generieren'
      ],
      currentCode: {
        backend: [
          '/opt/backend/routes/vehicle_training.py - Training & Generation',
          'MongoDB Collection: vehicle_trainings'
        ],
        frontend: [
          'src/components/VehicleDashboard.js',
          'src/components/VehicleGallery.js',
          'src/components/VehicleModifyModal.js'
        ]
      },
      problem: 'Feature existiert aber ist nicht im Hauptmenü verlinkt. Kein offizieller Launch.',
      plan: [
        '1. UI/UX Review',
        '2. Pricing festlegen',
        '3. In Hauptmenü integrieren oder separater Bereich'
      ]
    },
    {
      id: 'luma',
      name: '🎬 Luma Video Mode',
      status: 'experimental',
      statusLabel: '🔬 Experimental',
      description: 'Alternative Video-Generation mit Luma AI statt Kling/Hailuo.',
      howItWorks: [
        'Wie Beatleap, aber mit Luma Dream Machine',
        'Andere Ästhetik, andere Stärken'
      ],
      currentCode: {
        backend: [
          '/opt/backend/routes/luma_quick.py - /api/luma/generate/image, /api/luma/generate/video'
        ],
        frontend: [
          'src/components/LumaQuickMode.js'
        ]
      },
      problem: 'Parallel zu Beatleap entwickelt, aber nie offiziell gelauncht. Unklar ob nötig.',
      plan: [
        '1. Evaluieren ob Luma Vorteile gegenüber Kling/Hailuo hat',
        '2. Entweder in Beatleap integrieren oder entfernen'
      ]
    },
    {
      id: 'xai',
      name: '🤖 xAI / Grok Integration',
      status: 'testing',
      statusLabel: '🧪 Testing',
      description: 'Grok Image Edit für Remix-Funktion. Bild + Prompt → Variationen.',
      howItWorks: [
        'User wählt ein generiertes Bild',
        'Gibt Remix-Prompt ein (z.B. "mach es zu einem Gemälde")',
        'Grok generiert 4 Variationen',
        'User wählt die beste'
      ],
      currentCode: {
        backend: [
          '/opt/backend/routes/xai_media.py - /api/xai/image/edit',
          '/opt/backend/routes/grok_remix.py - /api/remix/grok'
        ],
        frontend: [
          'src/components/ImageRemixFullscreen.js'
        ]
      },
      problem: 'Aktuell nur für Admin sichtbar (isAdmin Check). Face-Preservation ist schwierig.',
      plan: [
        '1. Face-Preservation verbessern',
        '2. Kosten evaluieren (~$0.08/Request)',
        '3. Für alle User freischalten wenn gut genug'
      ]
    },
    {
      id: 'magic',
      name: '✨ Magic Create',
      status: 'paused',
      statusLabel: '⏸️ Pausiert',
      description: 'AI-gestützte Video-Erstellung mit Musik, Beats, automatischem Schnitt.',
      howItWorks: [
        'User wählt Musik oder lädt eigene hoch',
        'AI analysiert Beats',
        'AI wählt passende Bilder/Videos',
        'Automatischer Schnitt auf den Beat'
      ],
      currentCode: {
        backend: [
          '/opt/backend/routes/magic.py - /api/magic/*',
          '/opt/backend/services/magic_engine.py'
        ],
        frontend: [
          'src/components/MagicCreate.js'
        ]
      },
      problem: 'Komplexes Feature, viel Entwicklungsaufwand. Beatleap deckt ähnlichen Use-Case ab.',
      plan: [
        '1. Abwarten was User in Beatleap wollen',
        '2. Entscheiden ob Magic Create nötig ist'
      ]
    },
    {
      id: 'showcase',
      name: '🌟 Showcase / Julia',
      status: 'legacy',
      statusLabel: '📦 Legacy',
      description: 'Demo-Seite mit Julia-Avatar zum Ausprobieren ohne eigenes Training.',
      howItWorks: [
        'Besucher kann ohne Login Julia-Bilder generieren',
        'Zeigt was SNAPSHOT kann'
      ],
      currentCode: {
        backend: [
          '/opt/backend/routes/showcase.py - /showcase/julia'
        ],
        frontend: [
          'src/components/ShowcaseIDCard.js'
        ]
      },
      problem: 'Feature wird nicht aktiv beworben.',
      plan: [
        '1. Entscheiden ob Feature noch gebraucht wird'
      ]
    },
    // ============================================
    // 💰 SNAP SYSTEM RESILIENCE (2026-02-27)
    // ============================================
    {
      id: "snap-fallback",
      name: "💰 SNAP System Fallback & Resilience",
      status: "todo",
      statusLabel: "📋 TODO",
      description: "Absicherung des Snap-Abrechnungssystems gegen Ausfaelle und Race Conditions.",
      howItWorks: [
        "Aktuell: Minimale Absicherung - bei DB-Ausfall schlaegt Request fehl",
        "Problem: Race Conditions bei gleichzeitigen Requests moeglich",
        "Problem: Kein Retry bei temporaeren Fehlern",
        "Problem: Kein Alerting wenn Fehler auftreten"
      ],
      currentCode: {
        backend: [
          "/opt/backend/services/credits.py - CreditsService Klasse",
          "  charge_credits() - Zieht Snaps ab (Zeile 23-45)",
          "  get_user_credits() - Holt User-Balance (Zeile 13-18)",
          "  add_credits() - Fuegt Snaps hinzu (Zeile 47-65)",
          "",
          "/opt/backend/server.py - Bildgenerierung",
          "  Zeile 924: user_credits = await credits_service.get_user_credits()",
          "  Zeile 1227: await credits_service.charge_credits()",
          "",
          "/opt/backend/routes/beatleap.py - Video-Generierung",
          "  Zeile 1798-1814: Snap-Abzug fuer Videos",
          "  Zeile 1811: await credits_service.charge_credits()",
          "",
          "/opt/backend/routes/kontext_remix.py - Remix",
          "  Zeile 365: await credits_service.charge_credits()"
        ],
        frontend: [
          "src/components/CreditsDisplay.js - Zeigt Snap-Balance",
          "src/components/TransactionHistory.js - Kontoauszug"
        ]
      },
      problem: "Keine Absicherung gegen DB-Ausfaelle, Race Conditions theoretisch moeglich, kein Alerting.",
      plan: [
        "OPTION A: MongoDB Transactions (2-3h)",
        "  > Atomic Operations fuer garantierte Konsistenz",
        "  > Kein Doppel-Abzug bei gleichzeitigen Requests",
        "  > Datei: /opt/backend/services/credits.py",
        "",
        "OPTION B: Retry + Alerting (1h)",
        "  > 3x Retry bei temporaeren DB-Fehlern",
        "  > Telegram/Email Alert bei Fehlern",
        "  > Datei: /opt/backend/services/credits.py",
        "",
        "OPTION C: Graceful Degradation (1h)",
        "  > Bei Fehler: Generation trotzdem erlauben",
        "  > Snap-Abzug spaeter nachholen (Schulden-System)",
        "  > User-freundlich aber risikoreicher",
        "",
        "OPTION D: Alles zusammen (4-5h)",
        "  > Transactions + Retry + Alerting + Fallback",
        "  > Maximum Sicherheit"
      ]
    },
        // ============================================
    // 🐛 BUGS & FIXES (Audit 2026-02-25)
    // ============================================
    {
      id: 'bug-mongodb-count',
      name: '🐛 MongoDB count_documents Bug',
      status: 'bug',
      statusLabel: '🔴 Bug',
      description: 'MongoDB Atlas Query mit $exists funktioniert nicht korrekt bei count_documents.',
      howItWorks: [
        'Online-User Zählung im Dashboard',
        'Nutzt db.users.count_documents({last_activity: {$gte: ...}})',
        'Query gibt immer 0 zurück obwohl Daten existieren'
      ],
      currentCode: {
        backend: [
          '📁 /opt/backend/routes/admin.py',
          '📍 Zeile 82-113: online-users Endpoint',
          '🔗 Route: GET /api/admin/online-users',
          '⚠️ Zeile 82: count_documents({\"last_activity\": {\"$gte\": five_min_ago}})'
        ],
        frontend: [
          '📁 src/components/admin/AdminDashboard.js',
          '📍 fetchOnlineUsers() Funktion',
          '🔗 Ruft /api/admin/online-users auf'
        ]
      },
      problem: 'count_documents mit $exists/$gte gibt 0 zurück. Manuelles Iterieren findet die Daten. Wahrscheinlich MongoDB Atlas Driver Bug.',
      fehleranalyse: [
        '❌ Symptom: Dashboard zeigt 0 Online-User obwohl User aktiv sind',
        '🔍 Test: Manuelles db.users.find().to_list() + Python-Zählung = korrekte Werte',
        '🔍 Test: count_documents mit gleichem Filter = 0',
        '💡 Ursache: Motor/PyMongo + MongoDB Atlas Kombination',
        '📊 Betroffen: 26 User, alle haben last_activity, aber count = 0'
      ],
      plan: [
        '1. count_documents durch manuelle Zählung ersetzen (find + len)',
        '2. Oder: Aggregation Pipeline: [{$match: ...}, {$count: \"total\"}]',
        '3. Motor Version prüfen: pip show motor',
        '4. PyMongo Version prüfen: pip show pymongo'
      ]
    },
    {
      id: 'bug-guest-geo',
      name: '🐛 Guest Geo-Daten fehlen',
      status: 'bug',
      statusLabel: '🟡 Design',
      description: 'Guest-Visits werden ohne Geo-Daten (Land, Stadt) gespeichert.',
      howItWorks: [
        'Gast besucht Seite → guest_visits Eintrag',
        'Nur IP wird gespeichert, kein Country/City',
        'Geo-Daten werden erst bei API-Abruf live geholt'
      ],
      currentCode: {
        backend: [
          '📁 /opt/backend/routes/admin.py',
          '📍 Zeile 1669-1705: guest-stats Endpoint',
          '🔗 Route: GET /api/admin/guest-stats',
          '📁 /opt/backend/services/geo_cache.py (für Caching)',
          '⚠️ guest_visits Collection: nur ip, timestamp, referrer'
        ],
        frontend: [
          '📁 src/components/admin/AdminDashboard.js',
          '📍 fetchGuestStats() Funktion',
          '📍 Guest Traffic Monitor Sektion'
        ]
      },
      problem: 'Geo-Daten werden nicht persistiert. Bei späteren Analysen fehlen die Infos.',
      fehleranalyse: [
        '❌ Symptom: guest_visits hat country=None bei allen 19 Einträgen',
        '🔍 Test: DB-Query zeigt ip vorhanden, country fehlt',
        '💡 Ursache: Geo-Lookup passiert nur bei API-Abruf, nicht beim Insert',
        '📊 Betroffen: 100% der Guest-Visits (19 Einträge)'
      ],
      plan: [
        '1. Middleware: Beim Insert guest_visits → geo_cache abfragen',
        '2. Falls nicht in Cache → ip-api.com anfragen',
        '3. Country/City direkt in guest_visits speichern',
        '4. Rate-Limit: Max 45 req/min bei ip-api.com (free)'
      ]
    },
    {
      id: 'bug-orphan-generations',
      name: '🐛 225 Generations ohne avatar_id',
      status: 'data',
      statusLabel: '🟡 Daten',
      description: 'Alte Generierungen ohne Verknüpfung zum Avatar.',
      howItWorks: [
        'Generations Collection hat 319 Einträge',
        '225 davon haben kein avatar_id Feld',
        'Können nicht mehr einem Avatar zugeordnet werden'
      ],
      currentCode: {
        backend: [
          '📁 MongoDB Collection: generations',
          '⚠️ Pflichtfelder laut MEMORY.md: id, avatar_id',
          '📁 /opt/backend/server.py - Generation-Erstellung'
        ],
        frontend: [
          '📁 src/components/Gallery.js',
          '📍 Filter nach Avatar funktioniert nicht für alte Bilder'
        ]
      },
      problem: 'Historische Daten ohne Verknüpfung. Galerie-Filter funktioniert nicht für alte Bilder.',
      fehleranalyse: [
        '❌ Symptom: 225 von 319 Generations (70%) ohne avatar_id',
        '🔍 Test: db.generations.count_documents({avatar_id: null}) = 225',
        '💡 Ursache: Alte Code-Version hat avatar_id nicht gespeichert',
        '📅 Zeitraum: Wahrscheinlich vor einem bestimmten Commit'
      ],
      plan: [
        '1. Query: Finde user_id in orphan generations',
        '2. Für jeden user_id → finde zugehörigen Avatar (training_jobs)',
        '3. Migration: UPDATE generations SET avatar_id = X WHERE user_id = Y',
        '4. Falls 1 User mehrere Avatare: Zeitstempel vergleichen'
      ]
    },
    {
      id: 'deco-verified-users',
      name: '🎨 KPI "Verified Users" ist Deko',
      status: 'deco',
      statusLabel: '⚪ Deko',
      description: 'Die KPI-Karte für verifizierte User zeigt immer 0.',
      howItWorks: [
        'Dashboard zeigt "Verified Users" Statistik',
        'Sucht nach is_verified: true in users Collection',
        'Feld existiert nicht, daher immer 0'
      ],
      currentCode: {
        backend: [
          'User-Schema hat kein is_verified Feld',
          'Email-Verifikation wurde nie implementiert'
        ],
        frontend: [
          'AdminDashboard.js - KPI Karten'
        ]
      },
      problem: 'Feature existiert nicht, Anzeige ist irreführend.',
      plan: [
        '1. KPI-Karte entfernen ODER',
        '2. Email-Verifikation implementieren ODER',
        '3. Karte umbenennen zu etwas Sinnvollem'
      ]
    },
    {
      id: 'design-login-audit',
      name: '🎨 20.000+ Login-Audit-Einträge',
      status: 'design',
      statusLabel: '🟡 Design',
      description: 'Jeder Login erzeugt einen Audit-Eintrag mit Blockchain-Timestamp-Versuch.',
      howItWorks: [
        'User loggt sich ein → user_auth Audit-Eintrag',
        'OpenTimestamps wird versucht → meist "failed"',
        '~1000 Einträge pro Tag nur durch Logins'
      ],
      currentCode: {
        backend: [
          '/opt/backend/audit_middleware.py',
          '/opt/backend/routes/auth.py'
        ],
        frontend: [
          'SystemAuditHistory.js - zeigt alle Einträge'
        ]
      },
      problem: 'Blockchain-Timestamps für Logins sind Overkill. 924 "failed" Einträge. DB wird aufgebläht.',
      plan: [
        '1. Login-Einträge OHNE Blockchain-Timestamp erstellen',
        '2. Oder: Login-Audit komplett deaktivieren',
        '3. Oder: Nur wichtige Logins (Admin, nach X Tagen Pause) tracken'
      ]
    },
    {
      id: 'bug-float-credits',
      name: '🐛 Float Precision bei Credits',
      status: 'bug',
      statusLabel: '🔴 Bug',
      description: 'Credits werden als Float gespeichert → Rundungsfehler.',
      howItWorks: [
        'User kauft/verbraucht Credits',
        'Float-Arithmetik erzeugt: 30.33999999999997 statt 30.34',
        '9 von 26 Usern betroffen'
      ],
      currentCode: {
        backend: [
          '📁 /opt/backend/services/credits.py',
          '📍 Zeile 33-34: new_credits = current_credits - amount',
          '📍 Zeile 57: add_user_credits()',
          '📁 /opt/backend/database.py - update_user_credits()',
          '⚠️ MongoDB: credits Feld ist type Double'
        ],
        frontend: [
          '📁 src/components/admin/UserDetail.js',
          '📍 Credits Anzeige'
        ]
      },
      problem: 'JavaScript/Python Float Arithmetik ist ungenau. Bei vielen Transaktionen summiert sich der Fehler.',
      fehleranalyse: [
        '❌ Symptom: info@snapshotpro.ai hat credits=30.33999999999997',
        '🔍 Test: 9 von 26 Usern (35%) haben >2 Dezimalstellen',
        '💡 Ursache: Python float + MongoDB Double = Präzisionsverlust',
        '📊 Betroffene User: info@, kontakt@barisgreen, diana@, butterfly, pollmueller, katja, vali, andi, lian'
      ],
      plan: [
        '1. Option A: Credits × 100 als Integer (Cents)',
        '2. Option B: Decimal128 in MongoDB',
        '3. Quick-Fix: round(credits, 2) bei jedem Update',
        '4. Migration: UPDATE users SET credits = round(credits, 2)'
      ]
    },
    {
      id: 'bug-free-images',
      name: '🐛 free_images Logik-Fehler',
      status: 'bug',
      statusLabel: '🔴 Bug',
      description: 'free_images stimmt nicht mit earned - used überein.',
      howItWorks: [
        'User verdient free_images (Referrals, Bonus)',
        'User verbraucht free_images',
        'current sollte = earned - used sein'
      ],
      currentCode: {
        backend: [
          '📁 /opt/backend/services/credits.py',
          '📁 /opt/backend/routes/referral_bonus.py (Bonus vergeben)',
          '⚠️ MongoDB: users.free_images, users.free_images_earned, users.free_images_used',
          '⚠️ Logik: free_images = free_images_earned - free_images_used (SOLLTE!)'
        ],
        frontend: [
          '📁 src/components/admin/UserDetail.js',
          '📍 Zeigt free_images, earned, used an'
        ]
      },
      problem: 'julia@snapshotpro.ai: earned=750, used=463, current=87 (sollte 287 sein). Differenz: 200 Bilder!',
      fehleranalyse: [
        '❌ Symptom: free_images ≠ earned - used',
        '🔍 Test: julia@ hat earned=750, used=463, current=87',
        '🔍 Erwartung: 750 - 463 = 287, aber current = 87 → Differenz 200!',
        '💡 Mögliche Ursache: irgendwo wird free_images direkt gesetzt statt berechnet',
        '📊 Betroffen: Mindestens julia@, weitere User ungeprüft'
      ],
      plan: [
        '1. grep -r \"free_images\" /opt/backend/ → alle Stellen finden',
        '2. Prüfen wo free_images OHNE earned/used Update gesetzt wird',
        '3. Quick-Fix: db.users.updateOne({email:\"julia@...\"}, {$set:{free_images:287}})',
        '4. Langfrist: free_images immer als earned-used berechnen, nicht speichern'
      ]
    },
    {
      id: 'bug-tx-null-amount',
      name: '🐛 100+ Transactions mit amount=None',
      status: 'bug',
      statusLabel: '🔴 Bug',
      description: 'Credit Transactions haben kein amount Feld oder es ist None.',
      howItWorks: [
        'Admin gibt Credits/Bilder',
        'User verbraucht Credits',
        'Transaction wird erstellt'
      ],
      currentCode: {
        backend: [
          '📁 /opt/backend/routes/admin_credits.py',
          '📍 Zeile 65: amount_snaps statt amount',
          '📁 /opt/backend/services/credits.py',
          '⚠️ MongoDB Collection: credit_transactions',
          '⚠️ Schema: amount (manchmal), amount_snaps (manchmal), amount_eur'
        ],
        frontend: [
          '📁 src/components/admin/TransactionsList.js'
        ]
      },
      problem: '100+ Einträge mit amount=None. Schema inkonsistent: manchmal "amount", manchmal "amount_snaps".',
      fehleranalyse: [
        '❌ Symptom: 100+ Transactions zeigen keinen Betrag',
        '🔍 Test: db.credit_transactions.find({amount: null}).count() = 100+',
        '💡 Ursache: admin_credits.py nutzt amount_snaps, andere nutzen amount',
        '📊 Typen betroffen: admin_gift (3), admin_gift_images (3), usage (34), free_image_used (55), purchase (2)'
      ],
      plan: [
        '1. Schema definieren: immer "amount" für SNAPs',
        '2. admin_credits.py: amount statt amount_snaps',
        '3. Migration: db.credit_transactions.updateMany({amount:null}, [{$set:{amount:\"$amount_snaps\"}}])'
      ]
    },
    {
      id: 'bug-pending-payments',
      name: '🐛 9 Pending Payments nie abgeschlossen',
      status: 'bug',
      statusLabel: '🟡 Bug',
      description: 'payment_transactions bleiben auf "pending" Status.',
      howItWorks: [
        'User startet Stripe Checkout',
        'payment_transaction wird erstellt (pending)',
        'Nach Zahlung sollte Webhook Status updaten'
      ],
      currentCode: {
        backend: [
          '📁 /opt/backend/routes/stripe.py',
          '🔗 Route: POST /api/stripe/create-checkout-session',
          '🔗 Webhook: POST /api/stripe/webhook',
          '⚠️ MongoDB Collection: payment_transactions',
          '⚠️ Feld: payment_status (pending/completed)'
        ],
        frontend: [
          '📁 Checkout Flow in mehreren Komponenten'
        ]
      },
      problem: '9 Payments pending, 0 completed. Entweder Webhook-Problem oder User haben Checkout abgebrochen.',
      fehleranalyse: [
        '❌ Symptom: 9 pending, 0 completed in payment_transactions',
        '🔍 Test: db.payment_transactions.find({payment_status:\"completed\"}).count() = 0',
        '💡 Mögliche Ursachen: 1) Webhook nicht konfiguriert, 2) User hat abgebrochen, 3) Stripe Fehler',
        '📊 Sample: session_id=cs_live_a1GHq9..., amount=10.0 EUR, snaps_added=false'
      ],
      plan: [
        '1. Stripe Dashboard → Webhooks → Events prüfen',
        '2. stripe.py Webhook-Handler prüfen',
        '3. Alte pending (>24h) als "abandoned" markieren',
        '4. snaps_added=false → Credits nie gutgeschrieben?'
      ]
    },
    {
      id: 'deco-dsgvo-log',
      name: '🎨 DSGVO Löschungen ohne Collection',
      status: 'deco',
      statusLabel: '⚪ Deko',
      description: 'UI existiert, aber keine Daten werden geloggt.',
      howItWorks: [
        'Admin Panel hat "DSGVO Löschungen" Menüpunkt',
        'Soll Löschvorgänge protokollieren'
      ],
      currentCode: {
        backend: [
          'Keine dsgvo_delete_log Collection in DB',
          '/opt/backend/routes/ - unklar ob Route existiert'
        ],
        frontend: [
          'DsgvoDeleteLog.js'
        ]
      },
      problem: 'Collection existiert nicht. Entweder Feature nie implementiert oder falsche Collection.',
      plan: [
        '1. Prüfen ob DsgvoDeleteLog.js funktioniert',
        '2. Collection erstellen oder UI entfernen'
      ]
    },
    {
      id: 'data-packages-empty',
      name: '🗄️ Packages Collection leer',
      status: 'data',
      statusLabel: '🟡 Daten',
      description: 'Die packages Collection hat 0 Einträge.',
      howItWorks: [
        'User wählt SNAP-Paket zum Kaufen',
        'Pakete sollten in DB definiert sein'
      ],
      currentCode: {
        backend: [
          'MongoDB Collection: packages (leer!)',
          'Pakete vielleicht hardcoded?'
        ],
        frontend: [
          'PackageManagement.js'
        ]
      },
      problem: 'Keine Pakete in DB. Entweder hardcoded im Frontend oder andere Collection.',
      plan: [
        '1. Prüfen wo Pakete definiert sind',
        '2. Falls hardcoded: in DB migrieren',
        '3. PackageManagement UI prüfen'
      ]
    },
    {
      id: 'bug-pricing-null',
      name: '🐛 pricing_settings key/value = None',
      status: 'bug',
      statusLabel: '🟡 Bug',
      description: 'pricing_settings hat einen Eintrag mit key=None und value=None.',
      howItWorks: [
        'Admin setzt Preise im Admin Panel',
        'Preise werden in pricing_settings gespeichert'
      ],
      currentCode: {
        backend: [
          'MongoDB Collection: pricing_settings',
          '/opt/backend/routes/pricing.py'
        ],
        frontend: [
          'PriceListAdmin.js'
        ]
      },
      problem: 'Ein Eintrag hat keine Werte. Entweder fehlerhaftes Insert oder Schema-Problem.',
      plan: [
        '1. Defekten Eintrag finden und löschen',
        '2. Prüfen wie Preise gespeichert werden'
      ]
    },
    {
      id: 'info-maintenance-mode',
      name: 'ℹ️ Maintenance Mode nicht in DB',
      status: 'info',
      statusLabel: '🔵 Info',
      description: 'Wartungsmodus-Einstellung nicht in system_settings gefunden.',
      howItWorks: [
        'Admin aktiviert Wartungsmodus',
        'Sollte in system_settings gespeichert werden'
      ],
      currentCode: {
        backend: [
          '/opt/backend/routes/maintenance.py',
          'MongoDB Collection: system_settings'
        ],
        frontend: [
          'MaintenanceToggle.js'
        ]
      },
      problem: 'Kein maintenance_mode Eintrag in DB. Vielleicht wird er erst beim ersten Toggle erstellt.',
      plan: [
        '1. Wartungsmodus einmal aktivieren/deaktivieren',
        '2. Prüfen ob Eintrag erstellt wird'
      ]
    }
  ];

  const statusColors = {
    paused: 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400',
    beta: 'bg-blue-500/20 border-blue-500/30 text-blue-400',
    experimental: 'bg-purple-500/20 border-purple-500/30 text-purple-400',
    testing: 'bg-cyan-500/20 border-cyan-500/30 text-cyan-400',
    legacy: 'bg-gray-500/20 border-gray-500/30 text-gray-400',
    active: 'bg-green-500/20 border-green-500/30 text-green-400',
    bug: 'bg-red-500/20 border-red-500/30 text-red-400',
    data: 'bg-orange-500/20 border-orange-500/30 text-orange-400',
    deco: 'bg-slate-500/20 border-slate-500/30 text-slate-400',
    design: 'bg-amber-500/20 border-amber-500/30 text-amber-400',
    info: 'bg-blue-500/20 border-blue-500/30 text-blue-400'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center">
          <Box className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">Offene Features / To-Do Liste</h2>
          <p className="text-white/50 text-sm">{openFeatures.length} Features in Entwicklung</p>
        </div>
      </div>

      {/* Info Box */}
      <div className="p-4 rounded-xl bg-pink-500/10 border border-pink-500/30">
        <p className="text-pink-400 text-sm">
          🚧 Diese Features wurden angefangen aber nicht fertiggestellt. 
          Hier siehst du den aktuellen Stand und den Plan.
        </p>
      </div>

      {/* Features List */}
      <div className="space-y-3">
        {openFeatures.map(feature => (
          <div 
            key={feature.id}
            className="rounded-xl bg-white/5 border border-white/10 overflow-hidden"
          >
            {/* Feature Header - Clickable */}
            <button
              onClick={() => setExpandedFeature(expandedFeature === feature.id ? null : feature.id)}
              className="w-full p-4 flex items-center gap-3 text-left hover:bg-white/5 transition-all"
            >
              <span className="text-2xl">{feature.name.split(' ')[0]}</span>
              <div className="flex-1">
                <h3 className="text-white font-bold">{feature.name.slice(2).trim()}</h3>
                <p className="text-white/50 text-sm truncate">{feature.description}</p>
              </div>
              <span className={`px-3 py-1 rounded-lg text-xs font-medium border ${statusColors[feature.status]}`}>
                {feature.statusLabel}
              </span>
              <ChevronDown className={`w-5 h-5 text-white/50 transition-transform ${expandedFeature === feature.id ? 'rotate-180' : ''}`} />
            </button>

            {/* Expanded Content */}
            {expandedFeature === feature.id && (
              <div className="px-4 pb-4 space-y-4 border-t border-white/10 pt-4">
                {/* Description */}
                <div>
                  <h4 className="text-white/70 text-sm font-medium mb-2">📝 Was es macht</h4>
                  <p className="text-white/90 text-sm">{feature.description}</p>
                </div>

                {/* How it works */}
                <div>
                  <h4 className="text-white/70 text-sm font-medium mb-2">⚙️ Wie es funktioniert</h4>
                  <ul className="space-y-1">
                    {feature.howItWorks.map((step, i) => (
                      <li key={i} className="text-white/70 text-sm flex items-start gap-2">
                        <span className="text-[#10B981]">•</span>
                        {step}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Current Code */}
                <div>
                  <h4 className="text-white/70 text-sm font-medium mb-2">💻 Aktueller Code</h4>
                  <div className="space-y-2">
                    <div>
                      <p className="text-blue-400 text-xs font-medium mb-1">Backend:</p>
                      {feature.currentCode.backend.map((file, i) => (
                        <code key={i} className="block text-white/60 text-xs font-mono bg-black/30 px-2 py-1 rounded mb-1">{file}</code>
                      ))}
                    </div>
                    <div>
                      <p className="text-green-400 text-xs font-medium mb-1">Frontend:</p>
                      {feature.currentCode.frontend.map((file, i) => (
                        <code key={i} className="block text-white/60 text-xs font-mono bg-black/30 px-2 py-1 rounded mb-1">{file}</code>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Problem */}
                <div>
                  <h4 className="text-white/70 text-sm font-medium mb-2">⚠️ Problem</h4>
                  <p className="text-orange-400/90 text-sm bg-orange-500/10 p-2 rounded-lg">{feature.problem}</p>
                </div>

                {/* Plan */}
                <div>
                  <h4 className="text-white/70 text-sm font-medium mb-2">📋 Plan</h4>
                  <ul className="space-y-1">
                    {feature.plan.map((step, i) => (
                      <li key={i} className="text-white/70 text-sm flex items-start gap-2">
                        <span className="text-[#10B981]">→</span>
                        {step}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
